/**
 * Diagramme für die Auswertungen.
 *
 * Als SVG von Hand gezeichnet - eine Diagrammbibliothek würde für zwei
 * Balkendiagramme mehr Gewicht in die Anwendung bringen als der gesamte
 * übrige Code der Oberfläche.
 *
 * Die Gestaltung folgt festen Regeln:
 *   - Balken höchstens 24 px breit, oben abgerundet, an der Grundlinie
 *     eckig; zwischen benachbarten Balken bleibt eine Lücke in der
 *     Flächenfarbe stehen, statt sie mit einer Linie zu trennen.
 *   - Gitterlinien sind haarfein und durchgezogen, nie gestrichelt.
 *   - Beschriftungen tragen nie die Farbe der Datenreihe: Die Farbe steht
 *     im Balken, der Text bleibt in der Textfarbe. Sonst wird ein heller
 *     Farbton als Text unleserlich.
 *   - Ab zwei Datenreihen gibt es immer eine Legende.
 *   - Werte stehen nicht an jedem Balken, sondern im Tooltip - eine Zahl
 *     über jedem Balken liest niemand.
 */

import { useId, useState, type ReactNode } from 'react';

import { formatAmount } from '@erp/shared';

import { barPath, niceScale, shortAmount } from './chart-math';

// --- Tooltip -----------------------------------------------------------------

type TooltipState = { x: number; y: number; content: ReactNode } | null;

function Tooltip({ state }: { state: TooltipState }) {
  if (!state) return null;

  return (
    <div
      className="chart-tooltip"
      style={{ left: state.x, top: state.y }}
      role="tooltip"
      aria-hidden="true"
    >
      {state.content}
    </div>
  );
}

// --- Balkendiagramm, eine Reihe ---------------------------------------------

export type BarDatum = {
  /** Beschriftung auf der Achse. */
  label: string;
  /** Ausführliche Beschriftung für den Tooltip. */
  title: string;
  value: number;
  /** Zusatzzeile im Tooltip, z.B. die Belegzahl. */
  note?: string;
  /** Hebt den Balken hervor, etwa den heutigen Tag. */
  emphasized?: boolean;
};

export function BarChart({
  data,
  height = 200,
  /** Jede wievielte Achsenbeschriftung gezeigt wird. */
  labelEvery = 1,
  emptyText = 'Keine Daten im Zeitraum',
}: {
  data: BarDatum[];
  height?: number;
  labelEvery?: number;
  emptyText?: string;
}) {
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const titleId = useId();

  const width = 900;
  const padding = { top: 12, right: 8, bottom: 26, left: 58 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const maxValue = Math.max(0, ...data.map((entry) => entry.value));
  const { max, ticks } = niceScale(maxValue);

  if (data.length === 0) {
    return <div className="chart-empty">{emptyText}</div>;
  }

  const slot = plotWidth / data.length;
  // Höchstens 24 px, und zwischen den Balken bleibt Luft stehen.
  const barWidth = Math.max(2, Math.min(24, slot - 2));

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="chart-svg"
        role="img"
        aria-labelledby={titleId}
        preserveAspectRatio="none"
        onMouseLeave={() => setTooltip(null)}
      >
        <title id={titleId}>Umsatzverlauf</title>

        {/* Gitterlinien und Achsenbeschriftung */}
        {ticks.map((tick) => {
          const y = padding.top + plotHeight - (tick / max) * plotHeight;
          return (
            <g key={tick}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="var(--chart-grid)"
                strokeWidth={1}
              />
              <text
                x={padding.left - 8}
                y={y + 3.5}
                textAnchor="end"
                className="chart-axis-label"
              >
                {shortAmount(tick)}
              </text>
            </g>
          );
        })}

        {/* Grundlinie */}
        <line
          x1={padding.left}
          x2={width - padding.right}
          y1={padding.top + plotHeight}
          y2={padding.top + plotHeight}
          stroke="var(--chart-axis)"
          strokeWidth={1}
        />

        {data.map((entry, index) => {
          const barHeight = max > 0 ? (entry.value / max) * plotHeight : 0;
          const x = padding.left + index * slot + (slot - barWidth) / 2;
          const y = padding.top + plotHeight - barHeight;

          return (
            <g key={entry.label + index}>
              {/* Trefferfläche über die volle Höhe - ein 3 px hoher Balken
                  wäre sonst kaum zu treffen. */}
              <rect
                x={padding.left + index * slot}
                y={padding.top}
                width={slot}
                height={plotHeight}
                fill="transparent"
                onMouseEnter={(event) => {
                  const box = event.currentTarget.ownerSVGElement!.getBoundingClientRect();
                  const scale = box.width / width;
                  setTooltip({
                    x: (padding.left + index * slot + slot / 2) * scale,
                    y: Math.max(0, y * (box.height / height) - 8),
                    content: (
                      <>
                        <div className="chart-tooltip-title">{entry.title}</div>
                        <div className="chart-tooltip-value">{formatAmount(entry.value)} €</div>
                        {entry.note ? <div className="chart-tooltip-note">{entry.note}</div> : null}
                      </>
                    ),
                  });
                }}
              />
              {barHeight > 0 ? (
                <path
                  d={barPath(x, y, barWidth, barHeight)}
                  fill={entry.emphasized ? 'var(--series-2)' : 'var(--series-1)'}
                  pointerEvents="none"
                />
              ) : null}
            </g>
          );
        })}

        {/* Achsenbeschriftung unten */}
        {data.map((entry, index) =>
          index % labelEvery === 0 ? (
            <text
              key={`label-${index}`}
              x={padding.left + index * slot + slot / 2}
              y={height - 8}
              textAnchor="middle"
              className="chart-axis-label"
            >
              {entry.label}
            </text>
          ) : null,
        )}
      </svg>

      <Tooltip state={tooltip} />
    </div>
  );
}

// --- Balkendiagramm, zwei Reihen ---------------------------------------------

export type GroupedDatum = {
  label: string;
  title: string;
  current: number;
  previous: number;
};

export function GroupedBarChart({
  data,
  currentLabel,
  previousLabel,
  height = 240,
}: {
  data: GroupedDatum[];
  currentLabel: string;
  previousLabel: string;
  height?: number;
}) {
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const titleId = useId();

  const width = 900;
  const padding = { top: 12, right: 8, bottom: 26, left: 58 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const maxValue = Math.max(0, ...data.flatMap((entry) => [entry.current, entry.previous]));
  const { max, ticks } = niceScale(maxValue);

  const slot = plotWidth / data.length;
  // Zwei Balken je Gruppe, dazwischen 2 px Abstand in der Flächenfarbe.
  const barWidth = Math.max(3, Math.min(24, (slot - 10) / 2));

  return (
    <>
      <div className="chart-legend">
        <span className="chart-legend-entry">
          <span className="chart-swatch" style={{ background: 'var(--series-1)' }} />
          {currentLabel}
        </span>
        <span className="chart-legend-entry">
          <span className="chart-swatch" style={{ background: 'var(--series-2)' }} />
          {previousLabel}
        </span>
      </div>

      <div className="chart-wrap">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="chart-svg"
          role="img"
          aria-labelledby={titleId}
          preserveAspectRatio="none"
          onMouseLeave={() => setTooltip(null)}
        >
          <title id={titleId}>Monatsumsätze im Vergleich zum Vorjahr</title>

          {ticks.map((tick) => {
            const y = padding.top + plotHeight - (tick / max) * plotHeight;
            return (
              <g key={tick}>
                <line
                  x1={padding.left}
                  x2={width - padding.right}
                  y1={y}
                  y2={y}
                  stroke="var(--chart-grid)"
                  strokeWidth={1}
                />
                <text x={padding.left - 8} y={y + 3.5} textAnchor="end" className="chart-axis-label">
                  {shortAmount(tick)}
                </text>
              </g>
            );
          })}

          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + plotHeight}
            y2={padding.top + plotHeight}
            stroke="var(--chart-axis)"
            strokeWidth={1}
          />

          {data.map((entry, index) => {
            const groupX = padding.left + index * slot;
            const currentHeight = max > 0 ? (entry.current / max) * plotHeight : 0;
            const previousHeight = max > 0 ? (entry.previous / max) * plotHeight : 0;

            // Vorjahr links, laufendes Jahr rechts - zwischen den beiden
            // bleiben 2 px Fläche stehen.
            const previousX = groupX + slot / 2 - barWidth - 1;
            const currentX = groupX + slot / 2 + 1;

            return (
              <g key={entry.label}>
                <rect
                  x={groupX}
                  y={padding.top}
                  width={slot}
                  height={plotHeight}
                  fill="transparent"
                  onMouseEnter={(event) => {
                    const box = event.currentTarget.ownerSVGElement!.getBoundingClientRect();
                    const scaleX = box.width / width;
                    const scaleY = box.height / height;
                    const topY = padding.top + plotHeight - Math.max(currentHeight, previousHeight);

                    setTooltip({
                      x: (groupX + slot / 2) * scaleX,
                      y: Math.max(0, topY * scaleY - 8),
                      content: (
                        <>
                          <div className="chart-tooltip-title">{entry.title}</div>
                          <div className="chart-tooltip-row">
                            <span
                              className="chart-swatch"
                              style={{ background: 'var(--series-1)' }}
                            />
                            {currentLabel}
                            <strong>{formatAmount(entry.current)} €</strong>
                          </div>
                          <div className="chart-tooltip-row">
                            <span
                              className="chart-swatch"
                              style={{ background: 'var(--series-2)' }}
                            />
                            {previousLabel}
                            <strong>{formatAmount(entry.previous)} €</strong>
                          </div>
                        </>
                      ),
                    });
                  }}
                />

                {previousHeight > 0 ? (
                  <path
                    d={barPath(previousX, padding.top + plotHeight - previousHeight, barWidth, previousHeight)}
                    fill="var(--series-2)"
                    pointerEvents="none"
                  />
                ) : null}

                {currentHeight > 0 ? (
                  <path
                    d={barPath(currentX, padding.top + plotHeight - currentHeight, barWidth, currentHeight)}
                    fill="var(--series-1)"
                    pointerEvents="none"
                  />
                ) : null}
              </g>
            );
          })}

          {data.map((entry, index) => (
            <text
              key={`label-${entry.label}`}
              x={padding.left + index * slot + slot / 2}
              y={height - 8}
              textAnchor="middle"
              className="chart-axis-label"
            >
              {entry.label}
            </text>
          ))}
        </svg>

        <Tooltip state={tooltip} />
      </div>
    </>
  );
}

// --- Kennzahl ----------------------------------------------------------------

export function StatTile({
  label,
  value,
  note,
  delta,
  tone = 'neutral',
  hero = false,
}: {
  label: string;
  value: string;
  note?: string;
  /** Veränderung gegenüber einem Vergleichszeitraum, in Prozent. */
  delta?: { percent: number; label: string } | null;
  tone?: 'neutral' | 'warning' | 'danger';
  hero?: boolean;
}) {
  const toneColor =
    tone === 'warning' ? 'var(--warning)' : tone === 'danger' ? 'var(--danger)' : undefined;

  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div
        className="tile-value"
        style={{ ...(toneColor ? { color: toneColor } : {}), ...(hero ? { fontSize: 32 } : {}) }}
      >
        {value}
      </div>
      {delta ? (
        <div
          className="tile-note"
          style={{
            color:
              delta.percent > 0
                ? 'var(--success)'
                : delta.percent < 0
                  ? 'var(--danger)'
                  : 'var(--text-faint)',
          }}
        >
          {delta.percent > 0 ? '▲' : delta.percent < 0 ? '▼' : '='}{' '}
          {Math.abs(delta.percent).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %{' '}
          {delta.label}
        </div>
      ) : null}
      {note ? <div className="tile-note">{note}</div> : null}
    </div>
  );
}
